import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "src/components/ui/card";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/components/ui/tabs";
import { Textarea } from "src/components/ui/textarea";
import { fetchWithAuth } from "../utils/api";
import { useNavigate } from "react-router-dom";
import { Checkbox } from "src/components/ui/checkbox";
import { Loader2, Plus, Search, Trash2, Upload, Edit, Download, FileText, Video, BookOpen, Users, Eye, Clock, FileIcon, ArrowLeft } from "lucide-react";
import { Alert, AlertDescription } from "src/components/ui/alert";
import LoadingBar from '../components/ui/LoadingBar';
import { createClient } from '@supabase/supabase-js';
import { supabase, supabaseAdmin, isSupabaseConfigured, isSupabaseAdminConfigured, BUCKET_NAME, MATERIALS_FOLDER, useS3Protocol, createSignedUrl } from '../config/supabase';
import { supabaseUrl } from '../config/supabase';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import { Badge } from '../components/ui/badge';
import './MaterialsManagement.css';

// Define Material interface
interface Material {
  id: string;
  title: string;
  description: string;
  type: 'video' | 'article' | 'exercise' | 'template';
  level: 'beginner' | 'intermediate' | 'advanced';
  tags: string[];
  duration?: string;
  url: string;
  file_name: string;
  created_at: string;
  file_path?: string;
}

// Define new material form interface
interface NewMaterial {
  title: string;
  description: string;
  type: 'video' | 'article' | 'exercise' | 'template';
  level: 'beginner' | 'intermediate' | 'advanced';
  tags: string;
  duration?: string;
  file?: File | null;
}

const materialTypes = [
  { value: 'video', label: 'Video' },
  { value: 'article', label: 'Article' },
  { value: 'exercise', label: 'Exercise' },
  { value: 'template', label: 'Template' },
];

const materialLevels = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];

// Initialize with empty material
const initialNewMaterial: NewMaterial = {
  title: "",
  description: "",
  type: "article",
  level: "beginner",
  tags: "",
  duration: "",
  file: null
};

/**
 * Generate a properly signed URL using Supabase's createSignedUrl method
 * https://[project].supabase.co/storage/v1/object/sign/[bucket]/[filename]?token=[token]
 */
async function createProperSignedUrl(filename: string): Promise<string | null> {
  try {
    console.log(`Attempting to generate signed URL for file: ${filename}`);
    
    // Try to extract just the filename without any path information
    const cleanFilename = filename.split('/').pop() || filename;
    console.log(`Using clean filename: ${cleanFilename}`);
    
    // Use the helper function from config
    return await createSignedUrl(BUCKET_NAME, MATERIALS_FOLDER, cleanFilename);
  } catch (err) {
    console.error('Exception creating signed URL:', err);
    return null;
  }
}

// Function to view a material using a properly signed URL
const viewMaterial = async (material: Material) => {
  try {
    // External URLs (non-Supabase) can be opened directly
    if (material.url.startsWith('http') && !material.url.includes('supabase')) {
      window.open(material.url, '_blank');
      return;
    }
    
    // For Supabase files, we have several options to try
    
    // 1. If we have file_path, that's the most reliable source
    if (material.file_path) {
      toast.loading("Preparing secure access...", { id: "loading-resource" });
      
      // Try with the full path first (might include bucket name)
      let filename = material.file_path;
      
      // If it contains the bucket name, use it directly with createSignedUrl
      if (filename.includes(`${BUCKET_NAME}/`)) {
        const cleanFilename = filename.split(`${BUCKET_NAME}/`)[1];
        console.log(`Using file path without bucket prefix: ${cleanFilename}`);
        
        const signedUrl = await createProperSignedUrl(cleanFilename);
        toast.dismiss("loading-resource");
        
        if (signedUrl) {
          window.open(signedUrl, '_blank');
          return;
        }
      }
      
      // Otherwise try with the path as-is
      const signedUrl = await createProperSignedUrl(filename);
      toast.dismiss("loading-resource");
      
      if (signedUrl) {
        window.open(signedUrl, '_blank');
        return;
      }
    }
    
    // 2. If we have file_name, use that
    if (material.file_name) {
      toast.loading("Preparing secure access...", { id: "loading-resource" });
      
      const signedUrl = await createProperSignedUrl(material.file_name);
      toast.dismiss("loading-resource");
      
      if (signedUrl) {
        window.open(signedUrl, '_blank');
        return;
      }
    }
    
    // 3. Last resort: try to extract filename from URL
    let filename = null;
    
    if (material.url.includes('supabase')) {
      const urlPath = material.url.split('?')[0]; // Remove query params
      const pathParts = urlPath.split('/');
      filename = pathParts[pathParts.length - 1];
      
      if (filename) {
        toast.loading("Preparing secure access...", { id: "loading-resource" });
        
        const signedUrl = await createProperSignedUrl(filename);
        toast.dismiss("loading-resource");
        
        if (signedUrl) {
          window.open(signedUrl, '_blank');
          return;
        }
      }
    }
    
    // 4. If all else fails, try to open the URL directly
    window.open(material.url, '_blank');
    
  } catch (error) {
    console.error("Error viewing material:", error);
    toast.error("Failed to access resource");
    
    // Fallback to opening the original URL
    if (material.url) {
      window.open(material.url, '_blank');
    }
  }
};

const MaterialDetailsCard: React.FC<{ material: Material, onDelete: (id: string) => void, onEdit: (material: Material) => void }> = ({ material, onDelete, onEdit }) => {
  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-xl">{material.title}</CardTitle>
            <CardDescription className="mt-1.5 line-clamp-2">{material.description}</CardDescription>
          </div>
          <div className="flex gap-2">
            {material.type === 'video' && <Badge><Video size={14} className="mr-1" /> Video</Badge>}
            {material.type === 'article' && <Badge><FileText size={14} className="mr-1" /> Article</Badge>}
            {material.type === 'exercise' && <Badge><BookOpen size={14} className="mr-1" /> Exercise</Badge>}
            {material.type === 'template' && <Badge><FileIcon size={14} className="mr-1" /> Template</Badge>}
            <Badge variant={material.level === 'beginner' ? 'outline' : material.level === 'intermediate' ? 'default' : 'destructive'}>
              {material.level.charAt(0).toUpperCase() + material.level.slice(1)}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-grow">
        <div className="mt-2 space-y-2">
          {material.duration && (
            <div className="text-sm text-muted-foreground flex items-center">
              <Clock size={16} className="mr-1.5" /> {material.duration}
            </div>
          )}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {material.tags.map((tag, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between pt-4 border-t">
        <div className="text-sm text-muted-foreground">
          {material.created_at && new Date(material.created_at).toLocaleDateString()}
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            className="flex items-center gap-1.5"
            onClick={() => onEdit(material)}
          >
            <Edit size={16} />
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            className="flex items-center gap-1.5"
            onClick={() => viewMaterial(material)}
          >
            <Eye size={16} />
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            className="flex items-center gap-1.5 text-destructive hover:text-destructive"
            onClick={() => onDelete(material.id)}
          >
            <Trash2 size={16} />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

const MaterialsTable: React.FC<{ 
  materials: Material[], 
  onDelete: (id: string) => void, 
  onEdit: (material: Material) => void 
}> = ({ materials, onDelete, onEdit }) => {
  // Handle clicking the view button
  const handleViewClick = (material: Material) => {
    viewMaterial(material);
  };

  return (
    <div className="rounded-md border overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Level</TableHead>
            <TableHead>Tags</TableHead>
            <TableHead className="w-24">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {materials.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center p-4 text-muted-foreground">
                No materials found
              </TableCell>
            </TableRow>
          ) : (
            materials.map((material) => (
              <TableRow key={material.id}>
                <TableCell className="font-medium">{material.title}</TableCell>
                <TableCell>
                  {material.type === 'video' && (
                    <span className="inline-flex items-center gap-1">
                      <Video size={16} /> Video
                    </span>
                  )}
                  {material.type === 'article' && (
                    <span className="inline-flex items-center gap-1">
                      <FileText size={16} /> Article
                    </span>
                  )}
                  {material.type === 'exercise' && (
                    <span className="inline-flex items-center gap-1">
                      <BookOpen size={16} /> Exercise
                    </span>
                  )}
                  {material.type === 'template' && (
                    <span className="inline-flex items-center gap-1">
                      <FileIcon size={16} /> Template
                    </span>
                  )}
                </TableCell>
                <TableCell className="capitalize">{material.level}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {material.tags.slice(0, 3).map((tag, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                    {material.tags.length > 3 && (
                      <Badge variant="secondary" className="text-xs">
                        +{material.tags.length - 3}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => onEdit(material)}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewClick(material)}
                    >
                      <Eye size={16} />
                    </Button>
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => onDelete(material.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

const MaterialsManagement: React.FC = () => {
  const navigate = useNavigate();
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showNewMaterialForm, setShowNewMaterialForm] = useState(false);
  const [newMaterial, setNewMaterial] = useState<NewMaterial>(initialNewMaterial);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [showEditMaterialDialog, setShowEditMaterialDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState<Material | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [supabaseError, setSupabaseError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [successMessage, setSuccessMessage] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  // Check if Supabase is configured before performing operations
  const checkSupabaseConfig = () => {
    if (!isSupabaseConfigured()) {
      setSupabaseError("Supabase configuration is missing. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY environment variables.");
      return false;
    }
    return true;
  };

  // Check if Supabase Admin is configured for uploads
  const checkSupabaseAdminConfig = () => {
    if (!isSupabaseAdminConfigured()) {
      setSupabaseError("Supabase admin configuration is missing. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_SERVICE_KEY environment variables.");
      return false;
    }
    return true;
  };

  // Fetch materials on component mount
  useEffect(() => {
    fetchMaterials();
  }, []);

  // Add this useEffect after the existing useEffect for fetchMaterials
  useEffect(() => {
    // Check Supabase configuration on component mount
    const checkSupabase = async () => {
      try {
        if (!checkSupabaseConfig()) {
          return;
        }
        
        // Try both S3 and standard bucket listing to see which one works
        let bucketExists = false;
        let bucketListData = null;
        let error = null;
        
        // First try with S3 protocol - direct endpoint call
        try {
          console.log("Trying S3 protocol for bucket check...");
          
          // We'll use the REST API directly for S3 protocol
          const { data: s3Data, error: s3Error } = await supabase.storage.from(BUCKET_NAME).list(MATERIALS_FOLDER);
          
          if (!s3Error && s3Data) {
            console.log("S3 list successful:", s3Data);
            bucketExists = true;
            bucketListData = s3Data;
          } else if (s3Error) {
            console.warn("S3 protocol check failed:", s3Error);
            error = s3Error;
          }
        } catch (s3Err) {
          console.warn("S3 protocol bucket check exception:", s3Err);
        }
        
        // If S3 failed, try standard protocol
        if (!bucketExists) {
          try {
            console.log("Trying standard protocol for bucket check...");
            const { data: stdData, error: stdError } = await supabase.storage.listBuckets();
            
            if (stdError) {
              console.warn("Standard bucket list failed:", stdError);
              
              // If we already have an error from S3 attempt, keep it
              if (!error) error = stdError;
            } else if (stdData) {
              console.log("Standard bucket list returned:", stdData);
              bucketExists = stdData.some(bucket => bucket.name === BUCKET_NAME);
              bucketListData = stdData;
            }
          } catch (stdErr) {
            console.warn("Standard protocol bucket check exception:", stdErr);
          }
        }
        
        // Final decision based on all checks
        if (bucketExists) {
          console.log(`Bucket '${BUCKET_NAME}' exists! Contents:`, bucketListData);
          setSupabaseError("");
        } else {
          console.error(`Bucket '${BUCKET_NAME}' not found in list of buckets:`, bucketListData);
          setSupabaseError(`Supabase bucket '${BUCKET_NAME}' not found. Make sure this bucket exists in your Supabase storage and check S3 compatibility.`);
        }
      } catch (err: any) {
        console.error('Supabase check error:', err);
        setSupabaseError(`Supabase error: ${err.message}`);
      }
    };
    
    checkSupabase();
  }, []);

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth('/materials/');
      if (response && response.materials) {
        setMaterials(response.materials);
      }
    } catch (err: any) {
      console.error('Error fetching materials:', err);
      setError(err.message || 'Failed to fetch materials');
    } finally {
      setLoading(false);
    }
  };

  // Handle creating a new material
  const handleCreateMaterial = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMaterial.title || !newMaterial.description || !newMaterial.file) {
      setError("Please fill all required fields and upload a file");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      
      // Check for admin client configuration - we need this to bypass RLS
      if (!checkSupabaseAdminConfig()) {
        throw new Error("Failed to initialize Supabase admin client");
      }
      
      const file = newMaterial.file;
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `${MATERIALS_FOLDER}/${fileName}`;
      
      // 1. Upload file using admin client to bypass RLS
      console.log("Uploading file to path:", filePath);
      const { data: uploadData, error: uploadError } = useS3Protocol
        ? await supabaseAdmin.storage
            .from(BUCKET_NAME)
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: false
            })
        : await supabaseAdmin.storage
            .from(BUCKET_NAME)
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: false
            });
      
      if (uploadError) {
        console.error('Error uploading file:', uploadError);
        throw new Error(`Error uploading file: ${uploadError.message}`);
      }
      
      console.log("File uploaded successfully:", uploadData);

      // 2. Get a publicly accessible URL for the file
      const { data: publicUrlData } = supabaseAdmin
        .storage
        .from(BUCKET_NAME)
        .getPublicUrl(`${filePath}`);
        
      const publicUrl = publicUrlData.publicUrl;
      console.log("Generated public URL:", publicUrl);
      
      // Store the complete file path for easier retrieval
      const fullFilePath = `${BUCKET_NAME}/${filePath}`;
      
      // 3. Create the material record in the backend
      const tagsArray = newMaterial.tags.split(',').map(tag => tag.trim()).filter(Boolean);
      
      const materialData = {
        title: newMaterial.title,
        description: newMaterial.description,
        type: newMaterial.type,
        level: newMaterial.level,
        tags: tagsArray,
        duration: newMaterial.duration || null,
        url: publicUrl, // Use the public URL for immediate access
        file_name: fileName, // Store filename for future signed URL generation
        file_path: fullFilePath // Store the complete path including bucket name
      };

      // Send to backend
      const response = await fetchWithAuth('/admin/materials/', {
        method: 'POST',
        body: JSON.stringify(materialData)
      });

      // Reset form and show success message
      setNewMaterial(initialNewMaterial);
      setShowNewMaterialForm(false);
      setSuccessMessage("Material added successfully");
      
      // Refresh materials list
      fetchMaterials();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
      
    } catch (err: any) {
      console.error("Error creating material:", err);
      setError(err.message || "Failed to create material");
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  // Handle editing a material
  const handleUpdateMaterial = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingMaterial) return;
    
    try {
      setSubmitting(true);
      setError("");
      
      let updatedFileUrl = editingMaterial.url;
      let updatedFilePath = '';
      let updatedFileName = editingMaterial.file_name;
      
      // If a new file is uploaded, update it in Supabase
      if (newMaterial.file) {
        // Check for admin client configuration - we need this to bypass RLS
        if (!checkSupabaseAdminConfig()) {
          throw new Error("Failed to initialize Supabase admin client");
        }
        
        const file = newMaterial.file;
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
        const filePath = `${MATERIALS_FOLDER}/${fileName}`;
        
        // 1. Upload the new file using admin client to bypass RLS
        console.log("Uploading file to path:", filePath);
        const { data: uploadData, error: uploadError } = useS3Protocol
          ? await supabaseAdmin.storage
              .from(BUCKET_NAME)
              .upload(filePath, file, {
                cacheControl: '3600',
                upsert: false
              })
          : await supabaseAdmin.storage
              .from(BUCKET_NAME)
              .upload(filePath, file, {
                cacheControl: '3600',
                upsert: false
              });
        
        if (uploadError) {
          console.error('Error uploading file:', uploadError);
          throw new Error(`Error uploading file: ${uploadError.message}`);
        }
        
        console.log("File uploaded successfully:", uploadData);

        // 2. Get a publicly accessible URL for the file
        const { data: publicUrlData } = supabaseAdmin
          .storage
          .from(BUCKET_NAME)
          .getPublicUrl(`${filePath}`);
          
        const publicUrl = publicUrlData.publicUrl;
        console.log("Generated public URL:", publicUrl);
        
        updatedFileUrl = publicUrl;
        updatedFilePath = filePath;
        updatedFileName = fileName;
        
        // Store the complete file path for easier retrieval
        const fullFilePath = `${BUCKET_NAME}/${filePath}`;
        updatedFilePath = fullFilePath;
        
        if (!updatedFileUrl) {
          throw new Error("Failed to get URL for the uploaded file");
        }
        
        // Delete the old file from Supabase if we have path info
        if (editingMaterial.file_name) {
          const oldFilePath = `${MATERIALS_FOLDER}/${editingMaterial.file_name}`;
          await supabaseAdmin.storage.from(BUCKET_NAME).remove([oldFilePath]);
        }
      }

      // Parse tags
      const tagsArray = newMaterial.tags.split(',').map(tag => tag.trim()).filter(Boolean);
      
      // Update material in backend
      const materialData = {
        id: editingMaterial.id,
        title: newMaterial.title || editingMaterial.title,
        description: newMaterial.description || editingMaterial.description,
        type: newMaterial.type || editingMaterial.type,
        level: newMaterial.level || editingMaterial.level,
        tags: tagsArray,
        duration: newMaterial.duration || editingMaterial.duration,
        url: updatedFileUrl,
        file_name: updatedFileName,
        file_path: updatedFilePath || `${MATERIALS_FOLDER}/${editingMaterial.file_name}`
      };

      // Send to backend
      await fetchWithAuth(`/admin/materials/${editingMaterial.id}/`, {
        method: 'PUT',
        body: JSON.stringify(materialData)
      });

      // Reset form, close dialog and show success
      setNewMaterial(initialNewMaterial);
      setEditingMaterial(null);
      setShowEditMaterialDialog(false);
      setSuccessMessage("Material updated successfully");
      
      // Refresh materials list
      fetchMaterials();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
      
    } catch (err: any) {
      console.error("Error updating material:", err);
      setError(err.message || "Failed to update material");
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  // Handle deleting a material
  const handleDeleteMaterial = async () => {
    if (!materialToDelete) return;
    
    try {
      setSubmitting(true);
      
      // Delete the file from Supabase first using admin client to bypass RLS
      if (checkSupabaseAdminConfig() && materialToDelete.file_name) {
        const filePath = `${MATERIALS_FOLDER}/${materialToDelete.file_name}`;
        await supabaseAdmin.storage.from(BUCKET_NAME).remove([filePath]);
      }
      
      // Then delete the record from backend
      await fetchWithAuth(`/admin/materials/${materialToDelete.id}/`, {
        method: 'DELETE'
      });
      
      // Remove from UI state
      setMaterials(materials.filter(m => m.id !== materialToDelete.id));
      setSuccessMessage("Material deleted successfully");
      
      // Clean up
      setMaterialToDelete(null);
      setShowDeleteConfirmDialog(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
      
    } catch (err: any) {
      console.error("Error deleting material:", err);
      setError(err.message || "Failed to delete material");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle opening edit dialog
  const handleEditMaterial = (material: Material) => {
    setEditingMaterial(material);
    setNewMaterial({
      title: material.title,
      description: material.description,
      type: material.type,
      level: material.level,
      tags: material.tags.join(', '),
      duration: material.duration || '',
      file: null
    });
    setShowEditMaterialDialog(true);
  };

  // Handle opening delete confirmation dialog
  const handleDeleteConfirm = (material: Material) => {
    setMaterialToDelete(material);
    setShowDeleteConfirmDialog(true);
  };

  // Filter materials based on search and filters
  const filteredMaterials = materials.filter(material => {
    const matchesSearch = material.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          material.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          material.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesType = !selectedType || material.type === selectedType;
    const matchesLevel = !selectedLevel || material.level === selectedLevel;
    
    // Filter by tab
    if (activeTab !== "all" && material.type !== activeTab) {
      return false;
    }
    
    return matchesSearch && matchesType && matchesLevel;
  });

  // Function to get appropriate icon based on material type
  const getIcon = (type: string) => {
    switch(type) {
      case 'video': return <Video size={18} />;
      case 'article': return <FileText size={18} />;
      case 'exercise': return <Users size={18} />;
      case 'template': return <BookOpen size={18} />;
      default: return <FileText size={18} />;
    }
  };

  // Get level badge color
  const getLevelBadgeClass = (level: string) => {
    switch(level) {
      case 'beginner': return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case 'intermediate': return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
      case 'advanced': return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default: return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="motions-panel materials-panel">
      <LoadingBar isLoading={loading || submitting} />
      
      <div className="panel-header">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            aria-label="Go back"
            className="back-button"
          >
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="panel-title">Materials Management</h1>
            <p className="panel-subtitle">
              Manage debate practice materials for users
            </p>
          </div>
        </div>
        <Button onClick={() => setShowNewMaterialForm(true)}>
          <Plus size={18} className="mr-2" />
          Add Material
        </Button>
      </div>
      
      {/* Success & Error Messages */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {successMessage && (
        <Alert className="mb-6 bg-green-50 border-green-400 dark:bg-green-900/20 dark:border-green-600">
          <AlertDescription className="text-green-700 dark:text-green-400">{successMessage}</AlertDescription>
        </Alert>
      )}
      
      {supabaseError && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>
            {supabaseError} 
            <div className="mt-2 text-xs">
              <p>Add these environment variables to your project:</p>
              <code className="block p-2 mt-1 rounded overflow-auto" style={{ backgroundColor: 'var(--secondary-bg)', color: 'var(--text-color)' }}>
                REACT_APP_SUPABASE_URL=https://your-project-id.supabase.co<br/>
                REACT_APP_SUPABASE_ANON_KEY=your-anon-key
              </code>
            </div>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Search and Filter Controls */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input 
                  placeholder="Search materials..." 
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex gap-2 flex-wrap">
              <Select value={selectedType || ""} onValueChange={setSelectedType}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Types</SelectItem>
                  {materialTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedLevel || ""} onValueChange={setSelectedLevel}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Levels</SelectItem>
                  {materialLevels.map(level => (
                    <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {(selectedType || selectedLevel || searchQuery) && (
                <Button variant="outline" onClick={() => {
                  setSelectedType(null);
                  setSelectedLevel(null);
                  setSearchQuery('');
                }}>
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Materials Tabs */}
      <Tabs defaultValue="all" className="mb-6" value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All Materials</TabsTrigger>
          <TabsTrigger value="video">Videos</TabsTrigger>
          <TabsTrigger value="article">Articles</TabsTrigger>
          <TabsTrigger value="exercise">Exercises</TabsTrigger>
          <TabsTrigger value="template">Templates</TabsTrigger>
        </TabsList>
      </Tabs>
      
      {/* Materials Table */}
      <div className="flex justify-between items-center mt-6 mb-4">
        <Tabs defaultValue="table" className="w-full">
          <div className="flex justify-between items-center mb-4">
            <TabsList>
              <TabsTrigger value="table">Table View</TabsTrigger>
              <TabsTrigger value="grid">Grid View</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="table" className="p-0">
            <Card>
              <CardContent className="p-0">
                <MaterialsTable 
                  materials={filteredMaterials} 
                  onDelete={(id: string) => handleDeleteConfirm({ id } as Material)} 
                  onEdit={handleEditMaterial} 
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="grid" className="p-0">
            <Card>
              <CardContent className="p-6">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : filteredMaterials.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredMaterials.map(material => (
                      <MaterialDetailsCard 
                        key={material.id} 
                        material={material} 
                        onDelete={(id: string) => handleDeleteConfirm({ id } as Material)} 
                        onEdit={handleEditMaterial} 
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No materials found
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* New Material Dialog */}
      <Dialog open={showNewMaterialForm} onOpenChange={setShowNewMaterialForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Material</DialogTitle>
            <DialogDescription>
              Upload and add a new practice material for users
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateMaterial} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={newMaterial.title}
                  onChange={(e) => setNewMaterial({...newMaterial, title: e.target.value})}
                  placeholder="Enter material title"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="type">Material Type *</Label>
                <Select 
                  value={newMaterial.type} 
                  onValueChange={(value: any) => setNewMaterial({...newMaterial, type: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {materialTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={newMaterial.description}
                onChange={(e) => setNewMaterial({...newMaterial, description: e.target.value})}
                placeholder="Enter material description"
                required
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="level">Difficulty Level *</Label>
                <Select 
                  value={newMaterial.level} 
                  onValueChange={(value: any) => setNewMaterial({...newMaterial, level: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    {materialLevels.map(level => (
                      <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="duration">Duration (for videos)</Label>
                <Input
                  id="duration"
                  value={newMaterial.duration || ''}
                  onChange={(e) => setNewMaterial({...newMaterial, duration: e.target.value})}
                  placeholder="e.g. 10:30"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="tags">Tags (comma separated)</Label>
              <Input
                id="tags"
                value={newMaterial.tags}
                onChange={(e) => setNewMaterial({...newMaterial, tags: e.target.value})}
                placeholder="e.g. debate, rebuttal, beginner"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="file">Upload Material File *</Label>
              <div className="flex items-center gap-2">
                <input 
                  type="file" 
                  ref={fileInputRef}
                  onChange={(e) => setNewMaterial({
                    ...newMaterial, 
                    file: e.target.files ? e.target.files[0] : null
                  })}
                  className="hidden"
                  id="file"
                />
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload size={16} className="mr-2" />
                  Select File
                </Button>
                <div className="text-sm text-gray-500">
                  {newMaterial.file ? newMaterial.file.name : 'No file selected'}
                </div>
              </div>
            </div>
            
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            )}
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowNewMaterialForm(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Add Material'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Edit Material Dialog */}
      <Dialog open={showEditMaterialDialog} onOpenChange={setShowEditMaterialDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Material</DialogTitle>
            <DialogDescription>
              Update the material details
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleUpdateMaterial} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Title *</Label>
                <Input
                  id="edit-title"
                  value={newMaterial.title}
                  onChange={(e) => setNewMaterial({...newMaterial, title: e.target.value})}
                  placeholder="Enter material title"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-type">Material Type *</Label>
                <Select 
                  value={newMaterial.type} 
                  onValueChange={(value: any) => setNewMaterial({...newMaterial, type: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {materialTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description *</Label>
              <Textarea
                id="edit-description"
                value={newMaterial.description}
                onChange={(e) => setNewMaterial({...newMaterial, description: e.target.value})}
                placeholder="Enter material description"
                required
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-level">Difficulty Level *</Label>
                <Select 
                  value={newMaterial.level} 
                  onValueChange={(value: any) => setNewMaterial({...newMaterial, level: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    {materialLevels.map(level => (
                      <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-duration">Duration (for videos)</Label>
                <Input
                  id="edit-duration"
                  value={newMaterial.duration || ''}
                  onChange={(e) => setNewMaterial({...newMaterial, duration: e.target.value})}
                  placeholder="e.g. 10:30"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-tags">Tags (comma separated)</Label>
              <Input
                id="edit-tags"
                value={newMaterial.tags}
                onChange={(e) => setNewMaterial({...newMaterial, tags: e.target.value})}
                placeholder="e.g. debate, rebuttal, beginner"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-file">Replace File (optional)</Label>
              <div className="flex items-center gap-2">
                <input 
                  type="file" 
                  onChange={(e) => setNewMaterial({
                    ...newMaterial, 
                    file: e.target.files ? e.target.files[0] : null
                  })}
                  className="hidden"
                  id="edit-file"
                  ref={fileInputRef}
                />
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload size={16} className="mr-2" />
                  Select New File
                </Button>
                <div className="text-sm text-gray-500">
                  {newMaterial.file ? newMaterial.file.name : 'Keep existing file'}
                </div>
              </div>
            </div>
            
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            )}
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowEditMaterialDialog(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Material'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this material?<br />
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {materialToDelete && (
            <div className="py-4">
              <p className="font-medium">{materialToDelete.title}</p>
              <p className="text-sm text-gray-500">{materialToDelete.description}</p>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setShowDeleteConfirmDialog(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              variant="destructive" 
              onClick={handleDeleteMaterial}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Material'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaterialsManagement; 